import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useState } from "react";

import { parseDate } from "../../../helpers/date";
import {
  useGetProviderReprocessOptionsQuery,
  useReprocessAllForProviderMutation,
} from "../../../store/queries/ediConversion";

interface IReprocessAllModalProps {
  visible: boolean;
  onHide: () => void;
  // The page's toast, so the success/error message persists after this modal closes.
  toast: RefObject<Toast>;
}

/**
 * "Reprocess all for a provider" modal. The user picks a provider (only providers that actually have
 * EDI files are listed), sees its file count / total records / date span, and submits to queue a
 * reprocess of every file. The backend enqueues one durable job per file, oldest → newest; the
 * dedicated worker drains them one at a time, so this never overwhelms the app.
 */
function ReprocessAllModal({
  visible,
  onHide,
  toast,
}: IReprocessAllModalProps) {
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null
  );

  // Only fetch the (mildly expensive) aggregate while the modal is open.
  const { data, isLoading, isError } = useGetProviderReprocessOptionsQuery(
    undefined,
    { skip: !visible, refetchOnMountOrArgChange: true }
  );
  const [reprocessAll, { isLoading: isSubmitting }] =
    useReprocessAllForProviderMutation();

  const options = data?.data ?? [];
  const selected = options.find((o) => o.providerId === selectedProviderId);

  // Reset the selection whenever the modal is reopened.
  useEffect(() => {
    if (!visible) setSelectedProviderId(null);
  }, [visible]);

  const handleSubmit = async () => {
    if (!selectedProviderId) return;
    try {
      const result = await reprocessAll({
        providerId: selectedProviderId,
      }).unwrap();
      const { queued, skippedNoOriginal } = result.data;
      const skippedNote =
        skippedNoOriginal > 0
          ? ` ${skippedNoOriginal} file${
              skippedNoOriginal === 1 ? "" : "s"
            } skipped (no original EDI on file).`
          : "";
      toast.current?.show({
        severity: queued > 0 ? "success" : "warn",
        summary: "Reprocessing queued",
        detail:
          queued > 0
            ? `Queued ${queued} file${
                queued === 1 ? "" : "s"
              } to reprocess, oldest first.${skippedNote}`
            : `No files were queued.${skippedNote}`,
        life: 7000,
      });
      onHide();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Reprocess failed",
        detail:
          error?.data?.message ||
          error?.data?.messages?.[0] ||
          "Failed to queue reprocessing.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isSubmitting}
      />
      <Button
        label={
          selected
            ? `Reprocess ${selected.fileCount} file${
                selected.fileCount === 1 ? "" : "s"
              }`
            : "Reprocess all files"
        }
        icon="pi pi-refresh"
        onClick={handleSubmit}
        disabled={!selectedProviderId || isSubmitting}
        loading={isSubmitting}
      />
    </div>
  );

  return (
    <Dialog
      header="Reprocess all files for a provider"
      visible={visible}
      onHide={isSubmitting ? () => {} : onHide}
      closable={!isSubmitting}
      style={{ width: "32rem" }}
      footer={footer}
    >
      <p className="text-600 mt-0 line-height-3">
        This queues a reprocess of every EDI conversion file for the selected
        provider. Files run <strong>one at a time</strong>, from{" "}
        <strong>oldest to newest</strong>, so the account is reprocessed
        steadily in the background without overloading the app.
      </p>

      <label htmlFor="reprocess-provider" className="block font-medium mb-2">
        Provider
      </label>
      <Dropdown
        inputId="reprocess-provider"
        value={selectedProviderId}
        options={options.map((o) => ({
          label: `${o.providerName} (${o.fileCount} file${
            o.fileCount === 1 ? "" : "s"
          })`,
          value: o.providerId,
        }))}
        onChange={(e) => setSelectedProviderId(e.value)}
        placeholder={isLoading ? "Loading providers…" : "Select a provider…"}
        filter
        disabled={isLoading || isSubmitting}
        emptyMessage="No providers have EDI files."
        className="w-full"
      />

      {isError && (
        <small className="p-error block mt-2">
          Could not load providers. Please try again.
        </small>
      )}

      {selected && (
        <div className="mt-3 p-3 border-round surface-100">
          <div className="flex justify-content-between mb-2">
            <span className="text-600">EDI files</span>
            <span className="font-medium">
              {selected.fileCount.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-content-between mb-2">
            <span className="text-600">Total records</span>
            <span className="font-medium">
              {selected.totalRecords.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-content-between">
            <span className="text-600">Date range</span>
            <span className="font-medium">
              {parseDate(selected.oldestFileAt)} –{" "}
              {parseDate(selected.newestFileAt)}
            </span>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default ReprocessAllModal;
